import React, { FC } from 'react';
import flatMapDeep from 'lodash/flatMapDeep';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import RoutingUtils from '../../../shared/utils/utils-routing';
import { textColor, THEME_ALU, THEME_BLACK, THEME_PRIMARY } from '../../../styles/basics/colors';
import { Level1Out } from '../../../store/apiLibx';
import { useTranslation } from 'react-i18next';
import { MULTI_LANGUAGE_SHOP, useFeature } from '../../../shared/utils/togglz';
import { useAgeCategorySearchParam } from '../../../shared/utils/useAgeCategorySearchParam';

const StSidebarItem = styled.li<{ active?: boolean }>`
    & a {
        ${props => (props.active ? textColor(THEME_PRIMARY) : textColor(THEME_BLACK))};

        :hover {
            ${textColor(THEME_PRIMARY)};
        }
    }
`;

const StSidebarSubItem = styled(StSidebarItem)`
    padding-left: 15px;
`;

const StDocCount = styled.span`
    ${textColor(THEME_ALU)};
    font-size: 10px;
    margin-left: 0.3em;
`;

const StSidebar = styled.ul`
    width: 300px;
`;

interface SidebarItemProps {
    active: boolean;
    docCount: number;
    id: string;
    name: string;
}

const SidebarItem: FC<SidebarItemProps> = ({ active, docCount, id, name }) => {
    const { t } = useTranslation();
    const { searchParam: ageCategorySearchParam } = useAgeCategorySearchParam();

    return (
        <StSidebarItem active={active}>
            <Link
                to={RoutingUtils.libXLocalRegionPageLocationDescriptor({
                    level1: id,
                    ageCategory: ageCategorySearchParam
                })}
            >
                {name}
                <StDocCount>({t('numberFormat.numberWithoutDecimalPoint', { val: docCount })})</StDocCount>
            </Link>
        </StSidebarItem>
    );
};

interface SidebarSubItemProps {
    active: boolean;
    parentId: string;
    id: string;
    name: string;
    docCount: number;
    shopLanguage: string | undefined;
}

const SidebarSubItem: FC<SidebarSubItemProps> = ({ active, docCount, id, name, parentId, shopLanguage }) => {
    const { t } = useTranslation();
    const isMultiLanguageShopEnabled = useFeature(MULTI_LANGUAGE_SHOP);
    const { searchParam: ageCategorySearchParam } = useAgeCategorySearchParam();

    return (
        <StSidebarSubItem active={active}>
            <Link
                to={RoutingUtils.libXLocalRegionPageLocationDescriptor({
                    level1: parentId ?? '',
                    level2: id,
                    isMultiLanguageShopEnabled,
                    shopLanguage: shopLanguage,
                    ageCategory: ageCategorySearchParam
                })}
            >
                {name}
                <StDocCount>({t('numberFormat.numberWithoutDecimalPoint', { val: docCount })})</StDocCount>
            </Link>
        </StSidebarSubItem>
    );
};

interface SidebarProps {
    localRegions: Level1Out[];
    selectedRegion: string;
    shopLanguage: string | undefined;
}

export const LocalRegionsSidebar = (props: SidebarProps) => {
    return (
        <StSidebar>
            {flatMapDeep(props.localRegions, (level1, index) => [
                <SidebarItem
                    key={`sb_lr_${index}`}
                    name={level1.name}
                    id={level1.id}
                    docCount={level1.docCount ?? 0}
                    active={level1.id === props.selectedRegion}
                />,
                (level1.level2 && props.selectedRegion.split('->')[0] === level1.id ? level1.level2 : []).map(
                    (level2, index2) => (
                        <SidebarSubItem
                            key={`sb_lsr_${index2}`}
                            name={level2.name}
                            id={level2.id}
                            docCount={level2.docCount ?? 0}
                            active={level2.id === props.selectedRegion}
                            parentId={level1.id}
                            shopLanguage={props.shopLanguage}
                        />
                    )
                )
            ])}
        </StSidebar>
    );
};
